/**
 * Zod schemas for the `sessions` collection.
 *
 * Access model unified to a single audience reference (ADR-120): a session
 * targets exactly one Audience via `audienceId`. The Audience can be a
 * custom group, a role audience, a personal audience, or an anonymous
 * composition (created by the session compose flow when the organizer
 * mixes audiences/people).
 *
 * The CF-managed `targetedPersonIds[]` is copied directly from the linked
 * audience's `resolvedPersonIds[]`. The security rule reads only
 * `targetedPersonIds` and matches against `request.auth.token.personId`
 * (ADR-137). Pending people sit in the cache from the moment they're
 * added to the audience; they begin reading the instant their claim
 * stamps the personId custom claim on their Auth token.
 */

import { z } from 'zod';
import type { DateString, TimestampLike } from '../types/common';

export const sessionDocumentSchema = z.object({
  eventId: z.string(),
  title: z.string(),
  description: z.string().nullable(),

  /** References a session category entry in EventConfig.sessionTypes by ID. */
  sessionTypeId: z.string(),

  /**
   * The Audience whose members can read this session. Resolved via the
   * cascading-fanout trigger to `targetedPersonIds[]` below.
   *
   * Defaults populate at session create from
   * `EventConfig.sessionTypes[i].defaultAudienceId`. Subsequent edits to
   * the category config do NOT retroactively rewrite existing sessions —
   * once created, a session's `audienceId` is independent.
   */
  audienceId: z.string(),

  /**
   * CF-managed personId cache copied directly from the linked audience's
   * `resolvedPersonIds[]` (ADR-137). The security rule matches against
   * `request.auth.token.personId` — pending people are present in the
   * cache and begin reading the moment they claim. Clients cannot write
   * this — `affectedKeys` guard in firestore.rules.
   */
  targetedPersonIds: z.array(z.string()),

  /** Denormalized from startTime + Event.timezone by Cloud Functions. */
  date: z.custom<DateString>(),
  /** UTC — source of truth for chronological ordering. */
  startTime: z.custom<TimestampLike>(),
  endTime: z.custom<TimestampLike>(),

  /**
   * Reference to a document in `eventLocations`. Same model as before —
   * sessions inherit label and pin coordinates from the linked location.
   */
  locationId: z.string().nullable(),
  /** Denormalized label from the linked location. CF-managed. */
  locationLabel: z.string().nullable(),

  /** Firebase Storage URL to PDF. */
  materialsURL: z.string().nullable(),
  materialsFileName: z.string().nullable(),

  /**
   * Ordered personIds of the session's speakers (ADR-136 amendment).
   * Client-writable by per-event organizer/admin via SpeakerLinker;
   * pending and claimed persons accepted uniformly.
   */
  speakerIds: z.array(z.string()),
  /**
   * displayName denorm derived from `speakerIds` -> persons.displayName.
   * CF-managed by onSessionCreated/onSessionUpdated + onPersonUpdated
   * fanout.
   */
  speakerNames: z.array(z.string()),
});
